import { createFileRoute } from "@tanstack/react-router";
import { SeoPage } from "@/components/SeoPage";
import { faqJsonLd } from "@/lib/seo-jsonld";
import { breadcrumbJsonLd } from "@/lib/seo-jsonld";
import { KOMMUN_LINKS, SOURCE_FOHM_VAGLEDNING, SOURCE_ALKOHOLLAGEN, LAST_REVIEWED } from "@/lib/official-sources";

const faqs = [
  { q: "Vart ansöker jag om serveringstillstånd i Lidköping?", a: "Ansökan görs till tillståndsenheten i Lidköpings kommun. Verksamhet i annan kommun i Västra Götaland (t.ex. Skara, Vara, Götene) söker hos respektive kommun." },
  { q: "Vad kostar serveringstillstånd i Lidköping?", a: "Lidköpings kommun tar ut en ansökningsavgift och en årlig tillsynsavgift beroende på tillståndstyp och alkoholomsättning – aktuella avgifter finns på kommunens webbplats." },
  { q: "Hur lång är handläggningstiden i Lidköping?", a: "Räkna med 8–12 veckor för en komplett ansökan om stadigvarande tillstånd." },
];

export const Route = createFileRoute("/serveringstillstand-lidkoping")({
  component: () => (
    <SeoPage
      breadcrumbs={[
        { label: "Hem", to: "/" },
        { label: "Guider", to: "/serveringstillstand-och-kunskapsprov" },
        { label: "Lidköping" },
      ]}
      title="Serveringstillstånd Lidköping – ansökan och krav"
      intro="Allt du behöver veta om serveringstillstånd i Lidköping: var du ansöker, vilka krav som ställs och hur du förbereder dig för kunskapsprovet."
      lastReviewed={LAST_REVIEWED}
      sources={[KOMMUN_LINKS.lidkoping, SOURCE_FOHM_VAGLEDNING, SOURCE_ALKOHOLLAGEN]}
      sections={[
        { h2: "Var ansöker du i Lidköping?", body: "Tillståndsenheten i Lidköpings kommun ansvarar för serveringstillstånd. För verksamhet i grannkommuner som Skara, Vara, Götene eller Mariestad ansöker du hos respektive kommun." },
        { h2: "Krav från Lidköpings kommun", body: "Utöver de nationella kraven (personlig och ekonomisk lämplighet, godkänt kunskapsprov och lämplig lokal) har Lidköping en aktiv besöksnäring vid Vänern och Läckö slott. Uteserveringar och evenemangstillstånd är vanliga under sommarhalvåret. Från 1 juni 2026 är kraven på eget kök och varierat matutbud borttagna nationellt." },
        { h2: "Kunskapsprovet i Lidköping", body: "Provet genomförs hos kommunen efter att ansökan har kommit in. Skriftligt prov inom alkohollagen, serveringsbestämmelser, ordning, nykterhet och marknadsföring enligt FoHMFS 2014:7. Träna med Alkoholprovet i samma format." },
        { h2: "Handläggningstid", body: "Räkna med 8–12 veckor från komplett ansökan till beslut. Sök i god tid inför sommarsäsongen." },
        { h2: "Tillsyn och uppföljning", body: "Lidköpings kommun bedriver tillsyn tillsammans med polis och Skatteverket. Anmärkningar kan leda till erinran, varning, sanktionsavgift eller återkallat tillstånd." },
        { h2: "Lokala särdrag – Vänern och Läckö slott", body: "Lidköping ligger vid Vänerns strand med matnäring runt Läckö slott och Kållandsö. Säsongsvariationen är stor, vilket ofta gör tillfälliga tillstånd och uteserveringstillstånd centrala." },
      ]}
      faqs={faqs}
      related={{
        title: "Relaterade sidor",
        links: [
          { to: "/serveringstillstand-och-kunskapsprov", label: "Hub – alla guider" },
          { to: "/serveringstillstand-goteborg", label: "Serveringstillstånd Göteborg" },
          { to: "/serveringstillstand-skovde", label: "Serveringstillstånd Skövde" },
          { to: "/serveringstillstand-restaurang", label: "För restaurang" },
          { to: "/tillfalligt-serveringstillstand", label: "Tillfälligt tillstånd" },
          { to: "/serveringstillstand-catering", label: "För catering" },
          { to: "/kunskapsprov-alkohollagen", label: "Kunskapsprov i alkohollagen" },
          { to: "/foretag", label: "Företagslösning" },
        ],
      }}
    />
  ),
  head: () => ({
    meta: [
      { title: "Serveringstillstånd Lidköping – krav, prov & tips" },
      { name: "description", content: "Så får du serveringstillstånd i Lidköping: lokala krav, tillsynsavgifter och hur du klarar kunskapsprovet." },
      { property: "og:title", content: "Serveringstillstånd Lidköping – tips" },
      { property: "og:description", content: "Ansökan, krav, avgifter och kunskapsprov för serveringstillstånd i Lidköping." },
      { property: "og:url", content: "https://alkoholprovet.se/serveringstillstand-lidkoping" },
    ],
    links: [{ rel: "canonical", href: "https://alkoholprovet.se/serveringstillstand-lidkoping" }],
    scripts: [
      faqJsonLd(faqs),
      breadcrumbJsonLd([
        { label: "Hem", url: "https://alkoholprovet.se/" },
        { label: "Guider", url: "https://alkoholprovet.se/serveringstillstand-och-kunskapsprov" },
        { label: "Lidköping", url: "https://alkoholprovet.se/serveringstillstand-lidkoping" },
      ]),
    ],
  }),
});
